import { PromptFunction, ExecutionContext, ExecuteParams } from '../../types';
import { createCustomFunction } from './createFunction';
import { CoreEntityType, NovelActTree, NovelSchema } from '#/index';
import { NovelSceneUtils } from '../../utils';


const getOutlineByElements = (context: ExecutionContext, elements: ExecuteParams[]): NovelActTree[] => {
  const novelScene = new NovelSceneUtils(context);
  elements.forEach(element => {
    if (typeof element === 'object' && element.type === CoreEntityType.NovelActs) {
      novelScene.byAct(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelChapters) {
      novelScene.byChapter(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelScenes) {
      novelScene.byScene(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.Novels) {
      novelScene.byNovel(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.CodexEntries) {
      novelScene.byCodexEntry(element)
    } else if (typeof element === 'object' && element.type === 'codexDetail') {
      novelScene.byCodexDetail(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.CodexCategories) {
      novelScene.byCodexCategory(element)
    } else if (typeof element === 'object' && element.type === 'novelLabel') {
      novelScene.byLabel(element)
    } else if (typeof element === 'object' && element.type === 'pointOfView') {
      novelScene.byPov(element)
    } else if (typeof element === 'object' && element.type === 'textContainer' && element.reference) {
      novelScene.byActsTree(getOutlineByElements(context, [element.reference]))
    } else if (typeof element === 'object' && element.type === 'listContainer' && element.elements) {
      novelScene.byActsTree(getOutlineByElements(context, element.elements))
    }
  })
  return novelScene.getOutline();
}

/**
 * 小说相关函数
 * @returns 小说相关函数对象
 */
const novelsFunctions: Record<string, PromptFunction> = {
  novel: createCustomFunction(
    'novel',
    '返回当前小说（如果有）',
    (context): NovelSchema | void => {
      return context.globalContext.currentPosition?.novel;
    }
  ),
  'novel.author': createCustomFunction(
    'novel.author',
    '返回当前小说的作者',
    (context) => {
      const novel = context.globalContext.currentPosition?.novel;
      return novel?.attributes?.author || '';
    }
  ),
  'novel.title': createCustomFunction(
    'novel.title',
    '返回当前小说的标题',
    (context) => {
      const novel = context.globalContext.currentPosition?.novel;
      return novel?.attributes?.title || '';
    }
  ),
  'novel.language': createCustomFunction(
    'novel.language',
    '返回当前小说的语言',
    (context) => {
      const novel = context.globalContext.currentPosition?.novel;
      return novel?.attributes?.language || '';
    }
  ),
  'novel.tense': createCustomFunction(
    'novel.tense',
    '返回当前小说的时态',
    (context) => {
      const tense = context.globalContext.currentPosition?.novel?.attributes.tense || 'past';
      const tenseMap = {
        'past': '过去视角',
        'present': '现在视角'
      };
      return tense ? tenseMap[tense] : '';
    }
  ),
  'novel.hasSeries': createCustomFunction(
    'novel.hasSeries',
    '检查当前小说是否是系列的一部分',
    (context) => {
      const series = context.globalContext.currentPosition?.series;
      return Boolean(series);
    }
  ),
  'novel.fullText': createCustomFunction(
    'novel.fullText',
    '返回当前小说的全文层次结构，或给定参数的文本（例如，Acts、POV等）',
    (context, ...params: ExecuteParams[]) => {
      const outline = getOutlineByElements(context, params)
      return {
        type: 'outlineTree',
        data: outline,
        mode: 'content'
      };
    }
  ),
  'novel.outline': createCustomFunction(
    'novel.outline',
    '返回当前小说的完整大纲层次结构，或给定参数的大纲（例如，Acts、POV等）',
    (context, ...params: ExecuteParams[]) => {
      const outline = getOutlineByElements(context, params)
      return {
        type: 'outlineTree',
        data: outline,
        mode: 'summary'
      };
    }
  ),
}

export default novelsFunctions;